import React, { useState, useMemo, useEffect } from 'react';
import {
  Card, Button, Modal, Checkbox, Space, Tag, message, Row, Col, Typography,
  Divider, Alert, Statistic,
} from 'antd';
import {
  CheckCircleFilled, PlayCircleOutlined, PauseCircleOutlined,
  CheckOutlined, ExperimentOutlined,
} from '@ant-design/icons';
import { sprayService, weatherDataService } from '@iso/services';
import { sprayJobService } from '../../../services/sprayJob.service';
import useSprayPermissions from '../../../hooks/useSprayPermissions';
import { SPRAY_JOB_STATUS, SPRAY_PAUSE_REASON } from '@iso/constants/sprayJobStates';
import { computeDeltaT } from '@iso/lib/helpers/deltaT';
import PauseZoneModal from './PauseZoneModal';
import MixRecipe from './MixRecipe';

const { Text } = Typography;

// Helpers shared by the zone-start weather gate. Lifted to module level so
// they don't allocate inside renders.
const scopeOf = (value, min, max) => {
  if (value == null) return 'unknown';
  if (min != null && value < min) return 'out';
  if (max != null && value > max) return 'out';
  if (min == null && max == null) return 'unknown';
  return 'in';
};
const colourForScope = (s) => s === 'in' ? '#3f8600' : s === 'out' ? '#cf1322' : undefined;

/**
 * Unified single-page actions panel for the spray job (Damien, round-4
 * follow-up). Replaces the multi-page Prepare / Spray / Cleanup flow with
 * one stacked list of rows that drives the job state from
 * `to_be_started` → `completed` in place.
 *
 * Row kinds:
 *   - prepare: opens a modal with the PPE / equipment / weather checks.
 *     Done when status > to_be_started (i.e. operator confirmed prep).
 *   - zone:    Start / Pause / Finish buttons for one zone item.
 *   - refill:  "Begin refill" → "Done" button. Sets done_at on the marker.
 *   - cleanup: opens a modal with cleanup task / leftover-mix / PPE
 *     disposal confirmation. Drives the final status transitions.
 *
 * The clone-on-refill model is gone: a refill is a logged event on the
 * same spray_jobs row, not a new job spawned into spray history. The
 * actionRefill endpoint is still wired for legacy ad-hoc refills triggered
 * from the detail-page Pause modal, but the unified workflow never calls
 * it.
 */
const SprayJobActionsPanel = ({ job, onReload }) => {
  const { isAgronomist } = useSprayPermissions();
  const [busy, setBusy] = useState(false);
  const [prepareOpen, setPrepareOpen] = useState(false);
  const [cleanupOpen, setCleanupOpen] = useState(false);
  // pauseTargetIndex: the zone item index that the operator is pausing,
  // so we can replay-on-resume from the captured stop coordinates.
  const [pauseTargetIndex, setPauseTargetIndex] = useState(null);
  // Initial-fill acknowledgement checkbox state. Hotfix merge: the
  // standalone Initial Fill modal is gone — the ack lives inside the
  // merged Prepare modal alongside equipment / PPE / weather acks.
  // initialFillOpen / handleInitialFillConfirm were removed in PR #432
  // review (dead after the modal was deleted).
  const [initialFillAck, setInitialFillAck] = useState(false);
  // refillModalIndex: the index in zones JSON of the refill marker the
  // operator is acting on. Opens a modal that shows the Mix Recipe
  // recalculated for the refill's litres (may differ from initial fill).
  // Round-6: refill also re-confirms preparation-phase PPE before the
  // next leg starts. PPE list is the prep PPE from the job.
  const [refillModalIndex, setRefillModalIndex] = useState(null);
  const [refillAck, setRefillAck] = useState(false);
  const [refillPpeChecked, setRefillPpeChecked] = useState([]);
  // ppeCheckTargetIndex: the zone index whose PPE-check row is open
  // for confirmation. Each segment (initial + after each refill) gets
  // its own PPE check row.
  const [ppeCheckTargetIndex, setPpeCheckTargetIndex] = useState(null);
  const [ppeCheckChecked, setPpeCheckChecked] = useState([]);
  // zoneStartIndex: the index of the zone the operator is about to start.
  // Triggers a weather-check modal before the actual API call so we can
  // block (or warn agronomists) when conditions are out of scope.
  const [zoneStartIndex, setZoneStartIndex] = useState(null);
  const [zoneStartTasksChecked, setZoneStartTasksChecked] = useState([]);
  const [zoneStartOverrideAck, setZoneStartOverrideAck] = useState(false);
  // Live weather snapshot used by the zone-start gate. Fetched whenever
  // the modal opens so the operator sees current readings, not stale
  // numbers from page load.
  const [liveWeather, setLiveWeather] = useState(null);

  // --- prep modal local state ---
  const [prepEquipmentOk, setPrepEquipmentOk] = useState(false);
  const [prepWeatherOk, setPrepWeatherOk] = useState(false);
  // Prep tasks (mapping_status === 'to_be_started') the agronomist
  // configured on the job — operator must tick every one to confirm
  // preparation. Mirrors the old SprayJobPrepare page's task gate.
  const [prepTasksChecked, setPrepTasksChecked] = useState([]);
  // PPE for the preparation phase, ticked individually so the operator
  // physically goes through each item rather than blanket-confirming.
  const [prepPpeChecked, setPrepPpeChecked] = useState([]);

  // --- cleanup modal local state ---
  const [cleanupTasksChecked, setCleanupTasksChecked] = useState([]);
  const [cleanupLeftoverOk, setCleanupLeftoverOk] = useState(false);
  const [cleanupPpeOk, setCleanupPpeOk] = useState(false);

  const items = useMemo(() => {
    const raw = job?.zones;
    if (Array.isArray(raw)) return raw;
    if (typeof raw === 'string') {
      try { return JSON.parse(raw) || []; } catch (e) { return []; }
    }
    return [];
  }, [job?.zones]);

  // zone_progress is exposed by the backend as either an object or {}.
  const progress = useMemo(() => {
    const p = job?.zone_progress;
    if (!p || typeof p !== 'object') return {};
    return p;
  }, [job?.zone_progress]);

  const prepDone = job?.status && job.status !== SPRAY_JOB_STATUS.TO_BE_STARTED;
  const cleanupDone = job?.status === SPRAY_JOB_STATUS.COMPLETED;
  const initialFillDone = !!progress?.__initial_fill__?.done_at;

  // Per-task done state — persisted server-side under
  // zone_progress.__tasks__[task_name].done_at. The task list itself
  // lives on the spray job, so we re-derive the rows from job.tasks
  // and gate each by this map.
  const isTaskDone = (taskName) =>
    !!progress?.__tasks__?.[taskName]?.done_at;

  // PPE check done for the zone at items[zoneIndex]. Each "PPE check"
  // row precedes either the first zone or the first zone after each
  // refill — keyed by the zone item's index in the heterogeneous list.
  const isPpeCheckDone = (zoneIndex) =>
    !!progress?.__ppe_check__?.[String(zoneIndex)]?.done_at;

  // Round-6: the weather snapshot is now fetched at the moment Start
  // is pressed (inside openZoneStart) so the green-path call can fire
  // without a modal. The useEffect that previously tracked
  // zoneStartIndex is gone with it.

  // --- per-item completion + clickability helpers ---

  const isZoneDone = (idx) => !!progress?.[idx]?.finished_at;
  // A zone with started_at but no finished_at is either actively being
  // sprayed (job.status === 'spraying') or paused (the operator hit
  // Pause and stop-spraying flipped the job back to 'prepared'). The
  // distinction matters for the action buttons:
  //   in-progress → Pause + Finish enabled, Start disabled
  //   paused      → Start (resume) enabled, Finish disabled
  // Damien's round-4 follow-up: "if it is paused, finish should be
  // greyed out instead."
  const isZoneInProgress = (idx) =>
    !!progress?.[idx]?.started_at
    && !progress?.[idx]?.finished_at
    && job?.status === SPRAY_JOB_STATUS.SPRAYING;
  const isZonePaused = (idx) =>
    !!progress?.[idx]?.started_at
    && !progress?.[idx]?.finished_at
    && job?.status !== SPRAY_JOB_STATUS.SPRAYING;
  const isRefillDone = (item) => !!item?.done_at;

  /** All zone items before `idx` are finished and any refill before idx is done. */
  const allPriorComplete = (idx) => {
    for (let i = 0; i < idx; i += 1) {
      const it = items[i];
      if (!it) return false;
      if (it.type === 'refill') {
        if (!isRefillDone(it)) return false;
      } else if (!isZoneDone(i)) {
        return false;
      }
    }
    return true;
  };

  const allItemsComplete = () => {
    if (items.length === 0) return false;
    for (let i = 0; i < items.length; i += 1) {
      const it = items[i];
      if (it?.type === 'refill') {
        if (!isRefillDone(it)) return false;
      } else if (!isZoneDone(i)) {
        return false;
      }
    }
    return true;
  };

  // --- action handlers ---

  const handlePrepareConfirm = async () => {
    setBusy(true);
    try {
      // Hotfix: Prepare Spray and Initial Fill are now a single action
      // (Damien: the operator preps PPE / equipment / weather and
      // mixes the tank as one continuous step before pressing Start
      // on Zone 1). Confirming the merged modal:
      //   1. Calls /prepare to flip status to_be_started -> prepared
      //      (only when the job is still to_be_started — skip when
      //       re-opened for any reason).
      //   2. Calls /log-initial-fill to stamp zone_progress.__initial_fill__
      //      so the existing Zone-Start gate is satisfied without a
      //      separate Initial Fill row.
      if (job?.status === SPRAY_JOB_STATUS.TO_BE_STARTED) {
        const prepRes = await sprayJobService.prepare(job.id);
        if (prepRes?.code !== '0000') {
          message.error(prepRes?.message || 'Failed to confirm spray preparation');
          return;
        }
      }
      if (!progress?.__initial_fill__?.done_at) {
        const fillRes = await sprayService.logInitialFill(job.id);
        if (fillRes?.code !== '0000') {
          message.error(fillRes?.message || 'Failed to log initial fill');
          return;
        }
      }
      message.success('Spray prep & initial fill confirmed');
      setPrepareOpen(false);
      setPrepEquipmentOk(false);
      setPrepWeatherOk(false);
      setPrepTasksChecked([]);
      setPrepPpeChecked([]);
      setInitialFillAck(false);
      onReload && onReload();
    } catch (err) {
      // PR #432 review: the two sequential API calls share this catch,
      // so we can't tell which step failed — surface a message that
      // makes clear partial progress is safe to retry. Both sub-steps
      // are guarded by "already done" checks on retry, so re-confirming
      // is idempotent.
      message.error('Failed to complete prep — check connection and retry (partial progress is safe to retry)');
    } finally {
      setBusy(false);
    }
  };

  // Damien round-6: Start now fetches live weather first. If every
  // metric is within the job's configured limits, the API call fires
  // directly with the Delta T reading attached (no modal — just go).
  // If anything is out of scope, the modal opens for the override.
  // The spray-tasks list is gone from the modal too (there are no
  // spray-stage tasks any more).
  // Returns { snapshot, error }. snapshot has the parsed metrics
  // (each value or null when missing); error is set when the fetch
  // or the response shape itself failed — used by the Start gate to
  // distinguish "couldn't fetch" from "all metrics in scope" (PR
  // #430 review Warning #2: the previous version returned {} on
  // error which silently took the green path).
  const fetchLiveWeatherSnapshot = async () => {
    if (!job?.property_id) {
      return { snapshot: null, error: 'No property configured for this job.' };
    }
    try {
      const res = await weatherDataService.getRealtimeWeatherData(job.property_id);
      const v = res?.data?.values;
      if (!v || typeof v !== 'object') {
        return { snapshot: null, error: 'Weather data is unavailable for this property.' };
      }
      const humidity = v.humidity != null ? Number(v.humidity.toFixed(2)) : null;
      const temperature = v.temperature != null ? Number(v.temperature.toFixed(2)) : null;
      // Stull (2011) psychrometric — takes both temperature and
      // humidity so the gate's Delta T tracks the agronomic chart
      // contours instead of the humidity-only rule of thumb the
      // previous code used.
      const deltaTRaw = computeDeltaT(temperature, humidity);
      const deltaT = deltaTRaw != null ? Number(deltaTRaw.toFixed(2)) : null;
      return {
        snapshot: {
          wind: v.windSpeed != null ? Number((v.windSpeed * 3.6).toFixed(2)) : null,
          deltaT,
          rain: v.rainIntensity != null ? Number(v.rainIntensity.toFixed(2)) : null,
          temperature,
          humidity,
        },
        error: null,
      };
    } catch (e) {
      return { snapshot: null, error: 'Failed to reach the weather service.' };
    }
  };

  const computeOffending = (snap) => {
    const s = {
      Wind: scopeOf(snap?.wind, job?.wind_limit_min, job?.wind_limit_max),
      'Delta T': scopeOf(snap?.deltaT, job?.delta_t_limit_min, job?.delta_t_limit_max),
      Temperature: scopeOf(snap?.temperature, job?.temperature_min, job?.temperature_max),
      Rain: scopeOf(snap?.rain, null, job?.rain_limit),
    };
    return { scopes: s, offending: Object.entries(s).filter(([, x]) => x === 'out').map(([k]) => k) };
  };

  const sendStartZone = async (idx, snapshot) => {
    // Send the full snapshot — delta_t (Stull-computed), humidity,
    // temperature — so the action log row captures all three. The
    // post-completion "actual environmental range" panel on the
    // detail page aggregates these across zone_start entries.
    const body = {};
    if (snapshot?.deltaT != null) body.delta_t = snapshot.deltaT;
    if (snapshot?.humidity != null) body.humidity = snapshot.humidity;
    if (snapshot?.temperature != null) body.temperature = snapshot.temperature;
    try {
      const res = await sprayService.startZone(job.id, idx, body);
      if (res?.code === '0000') {
        message.success('Zone started');
        onReload && onReload();
      } else {
        message.error(res?.message || 'Failed to start zone');
      }
    } catch (err) {
      // Network / 5xx errors that throw — surface a toast so the
      // operator sees something happened (PR #430 review Warning #3:
      // the original code only handled non-0000 responses, letting
      // rejections vanish into the catch-less finally).
      message.error('Failed to start zone — please try again.');
    }
  };

  // Click handler for the Start button on a zone row. Always fetches
  // a fresh weather snapshot first so the gate runs against current
  // readings, not page-load state.
  const openZoneStart = async (idx) => {
    setBusy(true);
    try {
      const { snapshot, error } = await fetchLiveWeatherSnapshot();
      // Weather fetch failed — open the modal with a visible
      // "unavailable" alert and require the override checkbox before
      // Start can fire (PR #430 review Warning #2: silently taking
      // the green path on fetch failure bypassed the safety gate).
      if (error || !snapshot) {
        setZoneStartIndex(idx);
        setZoneStartOverrideAck(false);
        setLiveWeather({ __error__: error || 'Weather data unavailable.' });
        return;
      }
      const { offending } = computeOffending(snapshot);
      if (offending.length === 0) {
        // All green — go straight to start, no modal.
        await sendStartZone(idx, snapshot);
        return;
      }
      // Out of scope — open the override modal.
      setZoneStartIndex(idx);
      setZoneStartOverrideAck(false);
      setLiveWeather(snapshot);
    } catch (err) {
      message.error('Failed to start zone — please try again.');
    } finally {
      setBusy(false);
    }
  };

  // Confirm path from the override modal — same call but pulls the
  // delta T from the snapshot already on screen.
  const handleZoneStartConfirm = async () => {
    if (zoneStartIndex === null) return;
    setBusy(true);
    try {
      await sendStartZone(zoneStartIndex, liveWeather);
      setZoneStartIndex(null);
    } catch (err) {
      message.error('Failed to start zone — please try again.');
    } finally {
      setBusy(false);
    }
  };

  // Persist a task tick. Tasks are surfaced as their own action rows
  // now (Damien round-6, "actual rows in the actions"), so each tick
  // round-trips to the backend instead of staying as ephemeral client
  // state.
  const handleToggleTask = async (taskName) => {
    if (isTaskDone(taskName)) return; // one-way for now
    setBusy(true);
    try {
      const res = await sprayService.logTask(job.id, taskName);
      if (res?.code === '0000') {
        onReload && onReload();
      } else {
        message.error(res?.message || 'Failed to log task');
      }
    } catch (err) {
      message.error('Failed to log task');
    } finally {
      setBusy(false);
    }
  };

  const handlePpeCheckConfirm = async () => {
    if (ppeCheckTargetIndex === null) return;
    setBusy(true);
    try {
      const res = await sprayService.logPpeCheck(job.id, ppeCheckTargetIndex);
      if (res?.code === '0000') {
        message.success('PPE check confirmed');
        setPpeCheckTargetIndex(null);
        setPpeCheckChecked([]);
        onReload && onReload();
      } else {
        message.error(res?.message || 'Failed to log PPE check');
      }
    } catch (err) {
      message.error('Failed to log PPE check');
    } finally {
      setBusy(false);
    }
  };

  // handleInitialFillConfirm removed (PR #432 review): the standalone
  // Initial Fill modal it served was deleted when Prep and Initial
  // Fill were merged. The merged handlePrepareConfirm above now calls
  // sprayService.logInitialFill directly.

  // Damien round-6: no confirmation dialog — pressing Finish fires
  // immediately. The operator already knows when they're done with
  // the zone; the prompt was friction without value.
  const handleFinishZone = async (idx) => {
    setBusy(true);
    try {
      const res = await sprayService.finishZone(job.id, idx);
      if (res?.code === '0000') {
        message.success('Zone finished');
        onReload && onReload();
      } else {
        message.error(res?.message || 'Failed to finish zone');
      }
    } catch (err) {
      message.error('Failed to finish zone');
    } finally {
      setBusy(false);
    }
  };

  const handlePauseSubmit = async (payload) => {
    // PauseZoneModal returns the pause coords plus a `reason` (pause |
    // unplanned_refill) and an optional `liters` value. The pause
    // branch goes through stopSpraying as before. The unplanned-refill
    // branch hits the dedicated endpoint that ALSO injects a refill
    // marker BEFORE the active zone, so the existing planned-refill
    // workflow (Mix Recipe modal) gates the resume.
    const { reason, liters, ...pauseZone } = payload || {};
    const isUnplanned = reason === SPRAY_PAUSE_REASON.UNPLANNED_REFILL;
    setBusy(true);
    try {
      let res;
      if (isUnplanned) {
        res = await sprayService.pauseUnplannedRefill(job.id, {
          pauseZone,
          liters,
        });
      } else {
        res = await sprayService.stopSpraying(job.id, pauseZone);
      }
      if (res?.code === '0000') {
        message.success(
          isUnplanned
            ? 'Unplanned refill logged — complete the refill task before resuming'
            : 'Spray paused'
        );
        setPauseTargetIndex(null);
        onReload && onReload();
      } else {
        message.error(res?.message || 'Failed to pause');
      }
    } catch (err) {
      message.error('Failed to pause');
    } finally {
      setBusy(false);
    }
  };

  // Refill row now opens a modal showing the Mix Recipe recomputed for
  // the refill's litres. The recipe + acknowledgement live in the modal
  // so the operator sees the new doses before pressing continue.
  const openRefill = (idx) => {
    setRefillModalIndex(idx);
    setRefillAck(false);
  };

  const handleRefillConfirm = async () => {
    if (refillModalIndex === null) return;
    setBusy(true);
    try {
      const res = await sprayService.logRefill(job.id, refillModalIndex);
      if (res?.code === '0000') {
        message.success('Refill logged');
        setRefillModalIndex(null);
        setRefillAck(false);
        onReload && onReload();
      } else {
        message.error(res?.message || 'Failed to log refill');
      }
    } catch (err) {
      message.error('Failed to log refill');
    } finally {
      setBusy(false);
    }
  };

  const handleCleanupConfirm = async () => {
    setBusy(true);
    try {
      // PR #430 review Warning #4: this used to call
      // markAsWaitingForCompletion unconditionally — if the second
      // call (completeJob) failed, the job was stuck in
      // waiting_for_completion because retrying the modal hit the
      // wait endpoint with a now-wrong source state and early-
      // returned before completeJob got a chance.
      // Skip the wait step when the job is already in
      // waiting_for_completion so the retry path completes.
      if (job?.status !== SPRAY_JOB_STATUS.WAITING_FOR_COMPLETION) {
        const waitRes = await sprayService.markAsWaitingForCompletion(job.id);
        if (waitRes?.code !== '0000') {
          message.error(waitRes?.message || 'Failed to advance job status');
          return;
        }
      }
      const compRes = await sprayService.completeJob(job.id);
      if (compRes?.code !== '0000') {
        message.error(compRes?.message || 'Failed to complete job');
        return;
      }
      message.success('Spray job completed');
      setCleanupOpen(false);
      setCleanupTasksChecked([]);
      setCleanupLeftoverOk(false);
      setCleanupPpeOk(false);
      onReload && onReload();
    } catch (err) {
      message.error('Failed to complete job');
    } finally {
      setBusy(false);
    }
  };

  // --- row renderers ---

  const rowStyle = (done) => ({
    padding: '12px 14px',
    marginBottom: 10,
    borderRadius: 6,
    border: '1px solid',
    borderColor: done ? '#b7eb8f' : '#eee',
    background: done ? '#f6ffed' : '#fafafa',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    flexWrap: 'wrap',
  });

  const tick = (
    <CheckCircleFilled style={{ color: '#52c41a', fontSize: 20, marginRight: 8 }} />
  );

  // Prepare row — covers PPE / equipment / weather acks AND the
  // initial fill mix recipe (hotfix: merged into one action so the
  // operator confirms the whole pre-spray sequence in one modal).
  // "Done" requires both backend stamps: status > to_be_started and
  // zone_progress.__initial_fill__.done_at set.
  const prepareMergedDone = prepDone && initialFillDone;
  const prepareRow = (
    <div key="prepare-row" style={rowStyle(prepareMergedDone)}>
      <div>
        {prepareMergedDone && tick}
        <Text strong style={{ fontSize: 16 }}>Prepare spray & initial fill</Text>
        {!prepareMergedDone && (
          <div style={{ color: '#666', marginTop: 4 }}>
            Confirm PPE, equipment, weather, and the tank mix recipe before starting.
          </div>
        )}
      </div>
      <Button
        type={prepareMergedDone ? 'default' : 'primary'}
        disabled={prepareMergedDone || busy}
        onClick={() => setPrepareOpen(true)}
      >
        {prepareMergedDone ? 'Confirmed' : 'Begin prep'}
      </Button>
    </div>
  );

  const formatPoint = (letter, n1, n2) =>
    [letter, n1 ? `B${n1}` : null, n2 ? `R${n2}` : null].filter(Boolean).join(' · ') || '—';

  // PPE check rendering — a "Spray PPE check" row is required for the
  // first zone overall AND the first zone after each refill, because
  // those are the moments the operator goes (back) to spraying. The
  // row's GATE is keyed by the zone's item index, but the row RENDERS
  // earlier in the list: at the start of the refill run that precedes
  // the gated zone. This puts the PPE check ahead of the refill task
  // so the operator confirms spraying PPE BEFORE any chemical
  // handling, then refills, then resumes.
  //
  // ppeCheckRenderMap: <items index where the row should render> ->
  //                    <zone item index the PPE check gates>
  // ppeCheckGatedZones: <set of zone indices that need a PPE check>
  //                    used by canStart so each zone still knows
  //                    whether its check is required + done.
  const ppeCheckRenderMap = new Map();
  const ppeCheckGatedZones = new Set();
  {
    let lastWasRefill = true; // treat list start as "after a refill"
    let firstRefillInRun = -1; // first refill in current consecutive-refill run
    for (let i = 0; i < items.length; i += 1) {
      const it = items[i];
      if (it?.type === 'refill') {
        if (firstRefillInRun === -1) firstRefillInRun = i;
        lastWasRefill = true;
        continue;
      }
      if (lastWasRefill) {
        // Skip the PPE check when this zone has already been started —
        // the operator is already in spraying PPE from the original
        // start, and the Refill modal already re-confirms prep PPE for
        // the chemical handling. The unplanned-refill-mid-zone resume
        // hits this branch; Damien: "No need for the new Spray PPE
        // check every time, since the PPE check is for refill and it
        // is already in the refill form."
        const zoneAlreadyStarted = !!progress?.[i]?.started_at;
        if (!zoneAlreadyStarted) {
          const renderPos = firstRefillInRun >= 0 ? firstRefillInRun : i;
          ppeCheckRenderMap.set(renderPos, i);
          ppeCheckGatedZones.add(i);
        }
      }
      firstRefillInRun = -1;
      lastWasRefill = false;
    }
  }

  // Item rows. Each iteration may push more than one element (a PPE
  // check row before any zone where it's required, plus the zone /
  // refill row itself), so we use a manual reduce rather than a
  // direct .map.
  let zoneCount = 0;
  let refillCount = 0;
  const itemRows = items.reduce((acc, item, idx) => {
    // Inject the "Spray PPE check" row at this position when it's the
    // render-anchor for a gated zone (see ppeCheckRenderMap setup
    // above). The PPE check renders BEFORE any refill in the run so
    // the operator confirms spraying PPE before chemical handling.
    if (ppeCheckRenderMap.has(idx)) {
      const gateZoneIdx = ppeCheckRenderMap.get(idx);
      const done = isPpeCheckDone(gateZoneIdx);
      // Gate: prep done + initial fill done + every prior item
      // (zones and refills) before THIS render position must be done.
      // The PPE check itself doesn't wait on the refills it sits in
      // front of — that's the whole point of putting it ahead of the
      // refill task.
      let canClick = !done && prepDone && initialFillDone;
      if (canClick) {
        for (let j = idx - 1; j >= 0; j -= 1) {
          const it = items[j];
          if (it?.type === 'refill') {
            if (!isRefillDone(it)) { canClick = false; }
            break;
          }
          if (!isZoneDone(j)) { canClick = false; break; }
        }
      }
      acc.push((
        <div key={`ppe-check-${gateZoneIdx}`} style={rowStyle(done)}>
          <div>
            {done && tick}
            <Text strong style={{ fontSize: 16 }}>Spray PPE check</Text>
            <Text style={{ color: '#666', marginLeft: 8 }}>
              before next zone
            </Text>
            {!done && !canClick && (
              <div style={{ color: '#aaa', marginTop: 4, fontSize: 12 }}>
                Waiting on the previous step.
              </div>
            )}
          </div>
          <Button
            type={done ? 'default' : 'primary'}
            disabled={!canClick || busy}
            onClick={() => {
              setPpeCheckTargetIndex(gateZoneIdx);
              setPpeCheckChecked([]);
            }}
          >
            {done ? 'Confirmed' : 'Confirm PPE'}
          </Button>
        </div>
      ));
    }
    const renderedItem = (() => {
    if (item?.type === 'refill') {
      refillCount += 1;
      const done = isRefillDone(item);
      const canClick = !done && prepDone && allPriorComplete(idx);
      return (
        <div key={`refill-${idx}`} style={rowStyle(done)}>
          <div>
            {done && tick}
            <Text strong style={{ fontSize: 16 }}>Refill {refillCount}</Text>
            <Text style={{ color: '#666', marginLeft: 8 }}>
              {item.liters != null ? `${item.liters} L` : '—'}
            </Text>
            {/* unplanned refill markers are injected mid-spray when the
                operator runs out of mix — flag them so the operator
                can see this one wasn't on the original plan. */}
            {item.unplanned && (
              <Tag color="orange" style={{ marginLeft: 8 }}>Unplanned</Tag>
            )}
            {!done && !canClick && (
              <div style={{ color: '#aaa', marginTop: 4, fontSize: 12 }}>
                Finish the previous zones first.
              </div>
            )}
          </div>
          <Button
            type={done ? 'default' : 'primary'}
            disabled={!canClick || busy}
            onClick={() => openRefill(idx)}
          >
            {done ? 'Done' : 'Begin refill'}
          </Button>
        </div>
      );
    }
    zoneCount += 1;
    const done = isZoneDone(idx);
    const inProgress = isZoneInProgress(idx);
    const paused = isZonePaused(idx);
    const priorOk = allPriorComplete(idx);
    // Start: enabled for both untouched zones (priorOk required) and
    // paused zones (resume — backend preserves the original
    // started_at). Disabled while actively in progress or done. Also
    // gated on Initial Fill being acknowledged (round-5) and — when a
    // PPE check sits before this zone — that PPE check being
    // confirmed (round-6).
    const ppeReady = !ppeCheckGatedZones.has(idx) || isPpeCheckDone(idx);
    // Paused zones still require priorOk so an unplanned refill
    // injected between the original start and now actually gates
    // the resume. Without this the operator could press Resume on
    // a paused zone and skip the new refill + PPE-check rows the
    // backend inserted before it. For a normal pause (no refill
    // injection) priorOk is already true at the original start, so
    // this is a no-op.
    const canStart = !done
      && prepDone
      && initialFillDone
      && ppeReady
      && priorOk
      && (paused || !inProgress);
    // Pause: only meaningful while actively spraying.
    const canPause = inProgress;
    // Finish: only enabled while actively spraying. Paused zones must
    // be resumed (Start) before they can be Finished — see Damien's
    // round-4 follow-up.
    const canFinish = inProgress;
    return (
      <div key={`zone-${idx}`} style={rowStyle(done)}>
        <div style={{ minWidth: 0 }}>
          {done && tick}
          <Text strong style={{ fontSize: 16 }}>Zone {zoneCount}</Text>
          <Text style={{ color: '#666', marginLeft: 8 }}>
            {formatPoint(item.startLetter, item.startNumber1, item.startNumber2)}
            {' → '}
            {formatPoint(item.finishLetter, item.finishNumber1, item.finishNumber2)}
          </Text>
          {inProgress && (
            <Tag color="orange" style={{ marginLeft: 8 }}>In progress</Tag>
          )}
          {paused && (
            <Tag color="gold" style={{ marginLeft: 8 }}>Paused</Tag>
          )}
          {!done && !inProgress && !paused && !priorOk && (
            <div style={{ color: '#aaa', marginTop: 4, fontSize: 12 }}>
              Waiting on the previous step.
            </div>
          )}
        </div>
        <Space wrap>
          <Button
            icon={<PlayCircleOutlined />}
            type={canStart ? 'primary' : 'default'}
            disabled={!canStart || busy}
            onClick={() => openZoneStart(idx)}
          >
            {paused ? 'Resume' : 'Start'}
          </Button>
          <Button
            icon={<PauseCircleOutlined />}
            disabled={!canPause || busy}
            onClick={() => setPauseTargetIndex(idx)}
          >
            Pause
          </Button>
          <Button
            icon={<CheckOutlined />}
            disabled={!canFinish || busy}
            onClick={() => handleFinishZone(idx)}
          >
            Finish
          </Button>
        </Space>
      </div>
    );
    })();
    acc.push(renderedItem);
    return acc;
  }, []);

  const allDone = allItemsComplete();

  // ---- modals ----

  const ppePrep = Array.isArray(job?.ppe_requirements?.preparation)
    ? job.ppe_requirements.preparation
    : (Array.isArray(job?.ppe_requirements) ? job.ppe_requirements : []);
  const ppeSpraying = job?.ppe_requirements?.spraying || [];
  const prepTasks = (job?.tasks || []).filter((t) => t.mapping_status === SPRAY_JOB_STATUS.TO_BE_STARTED);
  const cleanupTasks = (job?.tasks || []).filter((t) => t.mapping_status === SPRAY_JOB_STATUS.SPRAY_CLEANUP);

  const allPrepPpeDone = ppePrep.length === 0
    || prepPpeChecked.length === ppePrep.length;
  const allRefillPpeDone = ppePrep.length === 0
    || refillPpeChecked.length === ppePrep.length;
  const allPpeSprayingChecked = ppeSpraying.length === 0
    || ppeCheckChecked.length === ppeSpraying.length;

  // Damien round-6: prep tasks moved out of the Prepare modal into
  // their own action rows.
  // Hotfix: Prepare and Initial Fill are now a single modal. The
  // initial-fill ack joins the existing equipment / weather / PPE
  // acks as a single gate. The mix-recipe ack is skipped (gate
  // satisfied) once the backend has already stamped
  // __initial_fill__.done_at — re-opening the modal on a partial
  // success doesn't require ticking it twice.
  const initialFillGate = initialFillDone || initialFillAck;
  const prepareCanConfirm = prepEquipmentOk
    && prepWeatherOk
    && allPrepPpeDone
    && initialFillGate;
  // Cleanup tasks also moved out — Cleanup modal gate is leftover mix
  // + PPE disposal only.
  const cleanupCanConfirm = cleanupLeftoverOk && cleanupPpeOk;
  // Refill modal: mix recipe acknowledgement + PPE re-confirm.
  const refillCanConfirm = refillAck && allRefillPpeDone;

  // Per-task gating: each task row is independently click-to-done.
  // Initial Fill is gated on all prep tasks being done; Cleanup is
  // gated on all cleanup tasks being done.
  const allPrepTasksDone = prepTasks.every((t) => isTaskDone(t.task_name));
  const allCleanupTasksDone = cleanupTasks.every((t) => isTaskDone(t.task_name));
  // ppeCheckRenderMap / ppeCheckGatedZones are defined above itemRows
  // so the canStart gate inside each zone render can consult them.

  return (
    <Card title="Actions" style={{ marginBottom: 16 }}>
      {items.length === 0 ? (
        <Alert
          type="warning"
          showIcon
          message="No zones configured"
          description="Add at least one zone to this spray job to use the actions workflow."
        />
      ) : (
        <>
          {prepareRow}

          {/* Prep tasks — one row per task (Damien round-6: "actual rows
              in the actions, rather than a component in a pre-existing
              row"). Each tick persists via /log-task so refresh keeps
              the operator's place. Initial Fill is gated on all prep
              tasks being done. */}
          {prepTasks.map((task) => {
            const done = isTaskDone(task.task_name);
            const canClick = !done && prepDone;
            return (
              <div key={`prep-task-${task.task_name}`} style={rowStyle(done)}>
                <div>
                  {done && tick}
                  <Text strong style={{ fontSize: 16 }}>Prep task</Text>
                  <Text style={{ color: '#666', marginLeft: 8 }}>
                    {task.task_name}
                  </Text>
                </div>
                <Button
                  type={done ? 'default' : 'primary'}
                  disabled={!canClick || busy}
                  onClick={() => handleToggleTask(task.task_name)}
                >
                  {done ? 'Done' : 'Mark done'}
                </Button>
              </div>
            );
          })}

          {/* Initial Fill is no longer a standalone action (hotfix
              merge): the mix recipe is shown inside the Prepare
              modal and the /log-initial-fill stamp is written when
              the modal is confirmed, so the Zone-Start gate that
              requires both prepDone + initialFillDone is satisfied
              by the merged Prepare row above. */}

          {itemRows}

          {/* Cleanup tasks — one row per task. Cleanup is gated on all
              of these being ticked. */}
          {cleanupTasks.map((task) => {
            const done = isTaskDone(task.task_name);
            // Cleanup tasks become clickable once every zone+refill
            // item is complete (so the operator is at the cleanup
            // phase, not mid-spray).
            const canClick = !done && allDone;
            return (
              <div key={`cleanup-task-${task.task_name}`} style={rowStyle(done)}>
                <div>
                  {done && tick}
                  <Text strong style={{ fontSize: 16 }}>Cleanup task</Text>
                  <Text style={{ color: '#666', marginLeft: 8 }}>
                    {task.task_name}
                  </Text>
                </div>
                <Button
                  type={done ? 'default' : 'primary'}
                  disabled={!canClick || busy}
                  onClick={() => handleToggleTask(task.task_name)}
                >
                  {done ? 'Done' : 'Mark done'}
                </Button>
              </div>
            );
          })}

          {/* Cleanup row — opens the leftover-mix + PPE-disposal modal.
              Gated on every zone+refill item being complete AND every
              cleanup task being ticked. */}
          <div key="cleanup-row" style={rowStyle(cleanupDone)}>
            <div>
              {cleanupDone && tick}
              <Text strong style={{ fontSize: 16 }}>Spray cleanup</Text>
              {!cleanupDone && (
                <div style={{ color: '#666', marginTop: 4 }}>
                  Confirm leftover mix and PPE disposal.
                </div>
              )}
              {!cleanupDone && allDone && !allCleanupTasksDone && (
                <div style={{ color: '#aaa', marginTop: 4, fontSize: 12 }}>
                  Finish cleanup tasks first.
                </div>
              )}
            </div>
            <Button
              type={cleanupDone ? 'default' : 'primary'}
              disabled={cleanupDone || !allDone || !allCleanupTasksDone || busy}
              onClick={() => setCleanupOpen(true)}
            >
              {cleanupDone ? 'Completed' : 'Begin cleanup'}
            </Button>
          </div>
        </>
      )}

      {/* Prepare modal — merged Prep + Initial Fill (hotfix). One
          confirmation gates both /prepare and /log-initial-fill. */}
      <Modal
        title="Prepare spray & initial fill"
        visible={prepareOpen}
        onCancel={() => setPrepareOpen(false)}
        onOk={handlePrepareConfirm}
        okText="Confirm prep & initial fill"
        okButtonProps={{ disabled: !prepareCanConfirm || busy, loading: busy }}
        width={640}
      >
        <Text>Confirm each item before continuing to the first zone.</Text>
        <Divider style={{ margin: '12px 0' }} />
        <div style={{ marginBottom: 12 }}>
          <Text strong>Equipment</Text>
          <ul style={{ paddingLeft: 20, color: '#666', margin: '4px 0' }}>
            <li>Tractor: {job?.tractor?.title || '—'}</li>
            <li>Sprayer: {job?.sprayer?.title || '—'}</li>
            <li>Nozzle: {job?.nozzle?.title || '—'}</li>
          </ul>
          <Checkbox
            checked={prepEquipmentOk}
            onChange={(e) => setPrepEquipmentOk(e.target.checked)}
          >
            Equipment is connected and in working condition
          </Checkbox>
        </div>
        <div style={{ marginBottom: 12 }}>
          <Text strong>PPE</Text>
          <div style={{ margin: '4px 0' }}>
            {ppePrep.length === 0 ? (
              <Text type="secondary"> none specified — tick "no PPE required" below to continue</Text>
            ) : (
              ppePrep.map((p) => (
                <div key={p}>
                  <Checkbox
                    checked={prepPpeChecked.includes(p)}
                    onChange={(e) => setPrepPpeChecked((prev) =>
                      e.target.checked
                        ? [...prev, p]
                        : prev.filter((x) => x !== p)
                    )}
                  >
                    {p}
                  </Checkbox>
                </div>
              ))
            )}
            {ppeSpraying.length > 0 && (
              <div style={{ marginTop: 6, fontSize: 12, color: '#888' }}>
                Spraying-phase PPE will be reconfirmed at the first Start: {ppeSpraying.join(', ')}
              </div>
            )}
          </div>
        </div>
        {/* Prep tasks are now their own action rows (round-6) — no
            longer rendered inside this modal. */}
        <div style={{ marginBottom: 12 }}>
          <Text strong>Weather</Text>
          <div style={{ color: '#666', margin: '4px 0' }}>
            Verify current wind, humidity, temperature and rain are within the job's
            environmental limits before starting.
          </div>
          <Checkbox
            checked={prepWeatherOk}
            onChange={(e) => setPrepWeatherOk(e.target.checked)}
          >
            Weather is within limits
          </Checkbox>
        </div>
        {/* Initial Fill mix recipe — merged into this modal. Initial
            fill is gated on the ack checkbox alongside the existing
            equipment / PPE / weather acks. */}
        <Divider style={{ margin: '12px 0' }} />
        <Text strong>Initial fill — mix recipe</Text>
        <div style={{ color: '#666', margin: '4px 0 8px', fontSize: 13 }}>
          Mix the tank to the doses below before starting the first zone.
        </div>
        <MixRecipe job={job} embedded />
        <div style={{ marginTop: 12 }}>
          <Checkbox
            checked={initialFillAck}
            onChange={(e) => setInitialFillAck(e.target.checked)}
          >
            Tank has been filled and mixed per the recipe above
          </Checkbox>
        </div>
      </Modal>

      {/* Pause modal — reuses the existing pause-zone capture widget */}
      <PauseZoneModal
        visible={pauseTargetIndex !== null}
        mode="pause"
        job={job}
        loading={busy}
        onCancel={() => setPauseTargetIndex(null)}
        onSubmit={handlePauseSubmit}
      />

      {/* Standalone Initial Fill modal removed — its mix recipe +
          acknowledgement are baked into the Prepare modal above.
          handleInitialFillConfirm + initialFillOpen state were
          dropped in the PR #432 review (dead after the modal was
          deleted). */}

      {/* Refill modal — Mix Recipe recomputes for the refill marker's
          litres (may differ from the initial fill), PLUS a prep-PPE
          re-confirm (round-6: refill is a Prepare-spray-before-refill
          moment, not just a tank top-up). The separate "Spray PPE
          check" row that follows the refill catches the spraying-phase
          PPE re-confirm. */}
      {refillModalIndex !== null && (() => {
        const refill = items[refillModalIndex];
        const refillLiters = refill?.liters;
        return (
          <Modal
            title="Refill — Mix Recipe & PPE"
            visible={refillModalIndex !== null}
            onCancel={() => {
              setRefillModalIndex(null);
              setRefillAck(false);
              setRefillPpeChecked([]);
            }}
            onOk={handleRefillConfirm}
            okText="Refill mixed — continue"
            okButtonProps={{ disabled: !refillCanConfirm || busy, loading: busy }}
            width={640}
          >
            <Text>
              Refill the tank with{' '}
              <Text strong>{refillLiters != null ? `${refillLiters} L` : '—'}</Text>{' '}
              and mix to the doses below. The recipe is recalculated for the
              refill amount, so the chemical quantities may differ from the
              initial fill.
            </Text>
            <Divider style={{ margin: '12px 0' }} />
            <MixRecipe job={job} waterQuantityOverride={refillLiters} embedded />
            <Divider style={{ margin: '12px 0' }} />
            <Checkbox
              checked={refillAck}
              onChange={(e) => setRefillAck(e.target.checked)}
            >
              Tank has been refilled and mixed per the recipe above
            </Checkbox>
            <Divider style={{ margin: '12px 0' }} />
            <Text strong>PPE (preparation phase)</Text>
            <div style={{ color: '#666', margin: '4px 0', fontSize: 13 }}>
              Re-confirm preparation PPE before continuing.
            </div>
            <div style={{ margin: '4px 0' }}>
              {ppePrep.length === 0 ? (
                <Text type="secondary">No prep PPE configured.</Text>
              ) : (
                ppePrep.map((p) => (
                  <div key={p}>
                    <Checkbox
                      checked={refillPpeChecked.includes(p)}
                      onChange={(e) => setRefillPpeChecked((prev) =>
                        e.target.checked
                          ? [...prev, p]
                          : prev.filter((x) => x !== p)
                      )}
                    >
                      {p}
                    </Checkbox>
                  </div>
                ))
              )}
            </div>
          </Modal>
        );
      })()}

      {/* Zone Start modal — only opens when the live-weather snapshot
          has at least one metric out of scope (round-6). Green path
          fires the API directly without a modal. Spray-stage tasks are
          gone too: there are no tasks during a spray any more, so the
          modal is just the weather override gate. */}
      {zoneStartIndex !== null && (() => {
        const weatherError = liveWeather?.__error__ || null;
        const scopes = {
          Wind: scopeOf(liveWeather?.wind, job?.wind_limit_min, job?.wind_limit_max),
          'Delta T': scopeOf(liveWeather?.deltaT, job?.delta_t_limit_min, job?.delta_t_limit_max),
          Temperature: scopeOf(liveWeather?.temperature, job?.temperature_min, job?.temperature_max),
          Rain: scopeOf(liveWeather?.rain, null, job?.rain_limit),
        };
        const offending = Object.entries(scopes)
          .filter(([, s]) => s === 'out')
          .map(([k]) => k);
        const hasOutOfScope = offending.length > 0;
        // Start gate: blocked by out-of-scope weather OR by an
        // unavailable weather snapshot. Both cases require the
        // operator to tick the override checkbox before the API
        // call fires (PR #430 review Warning #2).
        const startGate = (!hasOutOfScope && !weatherError) || zoneStartOverrideAck;
        // The zone label (Zone N) — compute from the index in the items list
        // by counting non-refill items up to and including this one.
        let zLabel = 1;
        for (let i = 0; i <= zoneStartIndex; i += 1) {
          if (items[i]?.type !== 'refill' && i < zoneStartIndex) zLabel += 1;
        }
        return (
          <Modal
            title={`Start Zone ${zLabel}`}
            visible={zoneStartIndex !== null}
            onCancel={() => setZoneStartIndex(null)}
            onOk={handleZoneStartConfirm}
            okText="Start spraying"
            okButtonProps={{ disabled: !startGate || busy, loading: busy }}
            width={640}
          >
            <Text strong>Current weather</Text>
            <div style={{ color: '#666', marginBottom: 8, fontSize: 13 }}>
              {liveWeather === null
                ? 'Loading…'
                : 'Compare against the job\'s configured limits.'}
            </div>
            <Row gutter={[16, 16]}>
              <Col xs={12} sm={6}>
                <Statistic
                  title="Wind"
                  value={liveWeather?.wind ?? '—'}
                  suffix={liveWeather?.wind != null ? 'km/h' : ''}
                  valueStyle={{ color: colourForScope(scopes.Wind) }}
                />
              </Col>
              <Col xs={12} sm={6}>
                <Statistic
                  title="Delta T"
                  value={liveWeather?.deltaT ?? '—'}
                  valueStyle={{ color: colourForScope(scopes['Delta T']) }}
                />
              </Col>
              <Col xs={12} sm={6}>
                <Statistic
                  title="Temperature"
                  value={liveWeather?.temperature ?? '—'}
                  suffix={liveWeather?.temperature != null ? '°C' : ''}
                  valueStyle={{ color: colourForScope(scopes.Temperature) }}
                />
              </Col>
              <Col xs={12} sm={6}>
                <Statistic
                  title="Rain"
                  value={liveWeather?.rain ?? '—'}
                  suffix={liveWeather?.rain != null ? 'mm/hr' : ''}
                  valueStyle={{ color: colourForScope(scopes.Rain) }}
                />
              </Col>
            </Row>
            {weatherError && (
              <Alert
                style={{ marginTop: 12 }}
                type="error"
                showIcon
                message="Weather data unavailable"
                description={
                  <div>
                    <div style={{ marginBottom: 8 }}>{weatherError}</div>
                    <Checkbox
                      checked={zoneStartOverrideAck}
                      onChange={(e) => setZoneStartOverrideAck(e.target.checked)}
                    >
                      {isAgronomist
                        ? 'I confirm I want to start the spray without a current weather reading (Agronomist override)'
                        : 'Has contacted Agronomist and got approval to spray without current weather data'}
                    </Checkbox>
                  </div>
                }
              />
            )}
            {hasOutOfScope && !weatherError && (
              <Alert
                style={{ marginTop: 12 }}
                type="warning"
                showIcon
                message={`Weather element${offending.length > 1 ? 's' : ''} out of scope`}
                description={
                  <div>
                    <div style={{ marginBottom: 8 }}>
                      <Text strong>{offending.join(', ')}</Text>{' '}
                      {`${offending.length > 1 ? 'are' : 'is'} outside the configured limits.`}
                    </div>
                    <Checkbox
                      checked={zoneStartOverrideAck}
                      onChange={(e) => setZoneStartOverrideAck(e.target.checked)}
                    >
                      {isAgronomist
                        ? 'I confirm I want to continue with the spray despite the out-of-scope weather (Agronomist override)'
                        : 'Has contacted Agronomist and got approval to spray'}
                    </Checkbox>
                  </div>
                }
              />
            )}
            {/* Spray-stage tasks have been removed (round-6 — Damien:
                "no need for extra tasks DURING a spray"). */}
          </Modal>
        );
      })()}

      {/* Spray PPE check modal — per-item PPE checklist; gates the
          start of the first zone (initial segment) and the first zone
          after each refill. */}
      <Modal
        title="Spray PPE check"
        visible={ppeCheckTargetIndex !== null}
        onCancel={() => {
          setPpeCheckTargetIndex(null);
          setPpeCheckChecked([]);
        }}
        onOk={handlePpeCheckConfirm}
        okText="Confirm PPE"
        okButtonProps={{ disabled: !allPpeSprayingChecked || busy, loading: busy }}
        width={520}
      >
        <Text>Re-confirm your spraying-phase PPE before continuing.</Text>
        <Divider style={{ margin: '12px 0' }} />
        {ppeSpraying.length === 0 ? (
          <Text type="secondary">No spraying-phase PPE configured for this job.</Text>
        ) : (
          ppeSpraying.map((p) => (
            <div key={p}>
              <Checkbox
                checked={ppeCheckChecked.includes(p)}
                onChange={(e) => setPpeCheckChecked((prev) =>
                  e.target.checked
                    ? [...prev, p]
                    : prev.filter((x) => x !== p)
                )}
              >
                {p}
              </Checkbox>
            </div>
          ))
        )}
      </Modal>

      {/* Cleanup modal */}
      <Modal
        title="Spray cleanup"
        visible={cleanupOpen}
        onCancel={() => setCleanupOpen(false)}
        onOk={handleCleanupConfirm}
        okText="Finish job"
        okButtonProps={{ disabled: !cleanupCanConfirm || busy, loading: busy }}
        width={560}
      >
        <Text>Confirm each item to mark the spray job complete.</Text>
        <Divider style={{ margin: '12px 0' }} />
        {/* Cleanup tasks are now their own action rows (round-6) — no
            longer rendered inside this modal. */}
        <div style={{ marginBottom: 12 }}>
          <Text strong>Leftover mix</Text>
          <div style={{ color: '#666', margin: '4px 0' }}>
            Dispose of leftover mix per product label and property policy.
          </div>
          <Checkbox
            checked={cleanupLeftoverOk}
            onChange={(e) => setCleanupLeftoverOk(e.target.checked)}
          >
            Leftover mix handled
          </Checkbox>
        </div>
        <div>
          <Text strong>PPE & equipment</Text>
          {/* Wrap the Checkbox in a div instead of overriding the
              component's own display rule — antd's Checkbox renders an
              inline-flex shell so the box and label align on the same
              baseline; forcing display:block on the outer span pushed
              the label onto its own line in some browsers. */}
          <div style={{ marginTop: 4 }}>
            <Checkbox
              checked={cleanupPpeOk}
              onChange={(e) => setCleanupPpeOk(e.target.checked)}
            >
              PPE & equipment cleaned / disposed of
            </Checkbox>
          </div>
        </div>
      </Modal>
    </Card>
  );
};

export default SprayJobActionsPanel;
